import { excludeFromFilteringColumnProperty } from '@client-shared/ui-agGrid';

import PercentageOrRawCellRenderer from './cellRenderers/percentageOrRawCellRenderer.component';
import EditUserSettingsCellRenderer from './cellRenderers/editUserSettingsCellRenderer.component';
import EditRoleCellRenderer from './cellRenderers/editRoleCellRenderer.component';
import UserClientsCheckboxCellRenderer from './cellRenderers/userClientsCheckboxCellRenderer';
import CompanyAssignmentsCheckboxCellRenderer from './cellRenderers/companyAssignmentsCheckBoxCellRenderer.component';
import saveOrCancelRenderer from './cellRenderers/saveOrCancelColumnRenderer.component';

import { VAFlagColumnDefinition, NOLFlagColumnDefinition, expirationSsoName, expirationDateColumnDefinition } from './columnDefinitions';
import { USER_SETTINGS_DEFAULT_COLSPAN } from './columnDefinitions/userSettings';

import {
  alignLeftCellStyle,
  alignRightCellStyle,
  alignCenterCellStyle,
} from '@common/cellStyles';
import { CellExportFormats } from '@common/xlsxExport/xlsxExport.constants';
import { userRolesDictionary } from './authorization/userRoles';
import CheckboxCellRenderer from './agGrid/cellRenderers/checkboxCellRenderer.component';
import ExpirationDateCellRenderer from './cellRenderers/expirationDateCellRenderer.component';

const emptyRenderer = () => null;

export const futureColumnDefinitionFactory = count => ({
  headerName: `Future ${count}`,
  field: `future${count}`,
  width: 140,
  minWidth: 120,
  ...alignLeftCellStyle,
});

export const ownershipColumnDefinitionFactory = count => ({
  headerName: `Ownership${count}`,
  field: `ownership${count}`,
  minWidth: 100,
  cellRenderer: PercentageOrRawCellRenderer,
  customExport: {
    format: CellExportFormats.PERCENT_6,
    omitIfNull: true,
  },
  ...alignRightCellStyle,
});

export const parentColumnDefinitionFactory = count => ({
  headerName: `Parent${count}`,
  field: `parent${count}`,
  minWidth: 80,
  ...alignCenterCellStyle,
});

export const editUserSettingsColumnDefinitionFactory = ({
  addingNewUser,
  loggedInUserSso,
  canEditHimself,
  canEditUsers,
  isUserModified,
  confirmModalData,
  userToEditSso,
  readOnly,
  showUserSettingsFormForEdit,
  showConfirmDialogModal,
  hideConfirmDialogModal,
}) => ({
  headerName: '',
  field: 'sso',
  cellRenderer: EditUserSettingsCellRenderer,
  minWidth: 40,
  width: 40,
  cellRendererParams: {
    addingNewUser,
    placeholderForNewUser: '<new user>',
    loggedInUserSso,
    canEditHimself,
    canEditUsers,
    isUserModified,
    confirmModalData,
    userToEditSso,
    readOnly,
    showUserSettingsFormForEdit,
    showConfirmDialogModal,
    hideConfirmDialogModal,
  },
  colSpan: params => {
    const { addingNewUser } = params.column.colDef.cellRendererParams;
    return addingNewUser && !params.data.sso.length ? USER_SETTINGS_DEFAULT_COLSPAN : 1;
  },
});

export const editRoleColumnDefinitionFactory = ({
  canModify,
  readOnly,
  onEditClick,
  onRemoveClick,
  editedRoleId,
}) => ({
  headerName: '',
  field: 'roleId',
  cellRenderer: EditRoleCellRenderer,
  width: 40,
  cellRendererParams: {
    canModify,
    readOnly,
    onEditClick,
    onRemoveClick,
    editedRoleId,
  },
  colSpan: params => {
    const { addingNewRole } = params.column.colDef.cellRendererParams;
    return addingNewRole && !params.data.sso.length ? USER_SETTINGS_DEFAULT_COLSPAN : 1;
  },
});

export const assignColumnDefinitionFactory = ({
  onChange,
  checkedClientsIds,
  editableClientId,
  readOnly,
  clients,
}) => ({
  headerName: 'Assign',
  field: 'clientId',
  ...excludeFromFilteringColumnProperty,
  cellRenderer: UserClientsCheckboxCellRenderer(),
  cellRendererParams: () => ({
    onChange,
    checkedClientsIds,
    editableClientId,
    readOnly,
    clients,
  }),
  width: 100,
  suppressSizeToFit: true,
});

export const userClientsRoleColumnDefinitionFactory = ({
  rolesOptions,
  selectRole,
  checkedClientsIds,
  editableClientId,
  readOnly,
}
) => ({
  headerName: 'Role',
  field: 'role',
  ...alignLeftCellStyle,
  cellEditor: 'agRichSelectCellEditor',
  cellEditorPopup: true,
  cellEditorParams: params => ({
    values: rolesOptions.find(option => option.clientId === params.data.clientId)
      .roles
      .filter(role => !role.disabled)
      .map(role => role.roleName),
    valueListMaxHeight: 'min(400px, 50vh)',
  }),
  valueFormatter: params => userRolesDictionary[params.value],
  editable: params => !readOnly
    && editableClientId === params.data.clientId
    && checkedClientsIds.includes(params.data.clientId),
  onCellValueChanged: params => selectRole({
    value: params.data.role,
    clientId: params.data.clientId,
    roleId: rolesOptions.find(option => option.clientId === params.data.clientId)
      .roles
      .find(({ roleName, disabled }) =>
        !disabled && roleName === params.data.role).roleId,
  }),
});

export const currentAssignmentsAssignColumnDefinitionFactory = ({
  field,
  onChange,
  checkedValues,
  assignmentType,
  readOnly = false,
  checkAll,
  allChecked = false,
}) => ({
  headerName: 'Assign',
  field,
  cellRenderer: CompanyAssignmentsCheckboxCellRenderer(),
  cellRendererParams: () => ({
    onChange,
    checkedValues,
    assignmentType,
    readOnly,
    checkAll,
    allChecked,
  }),
  width: 100,
  suppressSizeToFit: true,
});

export const saveOrCancelColumnDefinitionFactory = isEditable => ({
  cellEditor: saveOrCancelRenderer,
  cellRenderer: emptyRenderer,
  editable: isEditable,
  headerName: '',
  valueGetter: () => null,
  width: 20,
  ...alignCenterCellStyle,
});

export const NOLFlagColumnDefinitionWithEditorGenerator = (isEditable, nolOptions) => ({
  ...NOLFlagColumnDefinition,
  cellEditor: 'agSelectCellEditor',
  cellEditorParams: {
    values: ['', ...nolOptions],
  },
  editable: isEditable,
});

export const VAFlagColumnDefinitionWithEditorGenerator = (isEditable, vaOptions) => ({
  ...VAFlagColumnDefinition,
  cellEditor: 'agSelectCellEditor',
  cellEditorParams: {
    values: ['', ...vaOptions],
  },
  editable: isEditable,
});

export const clientFlagCheckboxControlsFactory = featureFlag => ({
  headerName: featureFlag.label,
  field: featureFlag.field,
  minWidth: 150,
  cellRenderer: CheckboxCellRenderer(),
  cellRendererParams: {
    isChecked: flagEnabled => flagEnabled,
    isDisabled: () => true,
  },
});

export const expirationDateColumnDefinitionFactory = ({
  disabled,
  checkedClientsIds,
  editableClientId,
  onChange,
  readOnly = false,
}) => ({
  ...expirationDateColumnDefinition,
  cellRenderer: ExpirationDateCellRenderer(),
  cellRendererParams: () => ({
    disabled,
    checkedClientsIds,
    editableClientId,
    onChange,
    readOnly,
  }),
});

export const expirationUpdatedByColumnDefinition = () => expirationSsoName;
